import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import { createApi } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../security";
import { addNotification } from "../../notifications/notificationsSlice";
import { AttorneyResponse, AttorneysView } from "./model/attorney";
import { CreateJobAttorneyFitRequest } from "../candidates-management/model/jobAttorneyFit";

// Define the types for the matching jobs API response
interface MatchingJob {
  job_id: number;
  headline: string;
  title_name: string;
  cities: Array<{ name: string; code: string }>;
  job_fit_status: "fit" | "not_fit_job" | null;
  _score: number;
  scores: {
    expertise: number;
    location: number;
    jd_year: number;
    education: number;
    employment_history: number;
    relocation: number | null;
    total: number;
  };
  job_title_id: number;
  job_folder_id: number;
  job_city: { id: number; name: string };
}

interface MatchingJobsResponse {
  not_fit_for_firm: boolean;
  reason?: string;
  matches: MatchingJob[];
  meta: {
    count: number;
    pages: number;
    page: number;
    per_page: number;
  };
}

const apiServerUrl = process.env.REACT_APP_HAISTACK_API_URL;

export const attorneyApi = createApi({
  reducerPath: "attorneyApi",
  tagTypes: ["Attorneys", "MatchingJobs", "JobAttorneyFits"],
  baseQuery: fetchBaseQuery({
    baseUrl: apiServerUrl,
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    }
  }),
  endpoints: (builder) => ({
    // result type = EmployeesView, arg type = string
    getAttorneys: builder.query<AttorneysView, string>({
      query: (qs) => `/api/v2/attorneys?${qs}`,
      // server returns AttorneyResponse; map it to AttorneysView
      transformResponse: (response: AttorneyResponse): AttorneysView => ({
        data: response.attorneys,
        count: response.meta?.count ?? 0
      }),
      providesTags: ["Attorneys"]
    }),

    // New endpoint for getting matching jobs for an attorney
    getMatchingJobs: builder.query<
      MatchingJobsResponse,
      {
        attorneyId: string;
        employerId?: number;
        page?: number;
        perPage?: number;
      }
    >({
      query: ({ attorneyId, employerId, page = 1, perPage = 25 }) => {
        const params = new URLSearchParams();
        if (employerId) params.append("employer_id", employerId.toString());
        if (page) params.append("page", page.toString());
        if (perPage) params.append("per_page", perPage.toString());

        const queryString = params.toString();
        return `/api/v2/attorneys/${attorneyId}/matching_jobs${
          queryString ? `?${queryString}` : ""
        }`;
      },
      providesTags: (result, error, { attorneyId }) => [
        { type: "MatchingJobs", id: attorneyId }
      ]
    }),

    // Create job attorney fit endpoint
    createJobAttorneyFit: builder.mutation<any, CreateJobAttorneyFitRequest>({
      query: (request) => ({
        url: "/api/v2/job_attorney_fits",
        method: "POST",
        body: request
      }),
      invalidatesTags: (result, error, { attorney_id }) => [
        { type: "MatchingJobs", id: attorney_id },
        "JobAttorneyFits"
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Attorney added to job successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (error) {
          dispatch(
            addNotification({
              message: "Failed to add attorney to job",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    })
  })
});

export const { 
  useGetAttorneysQuery, 
  useGetMatchingJobsQuery, 
  useCreateJobAttorneyFitMutation 
} = attorneyApi;
